/*
 * *****************************************************************************
 *     Copyright (C)  Motorola Solutions, INC.
 *     All Rights Reserved.
 *     Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, exhaustMap, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { defer, of, retry, timer } from 'rxjs';
import {
    fetchAbandonedCallsRoutingSettings,
    fetchAbandonedCallsRoutingSettingsFail,
    fetchAbandonedCallsRoutingSettingsSuccess,
    fetchAgencySettings,
    fetchAgencySettingsFailure,
    fetchAgencySettingsSuccess,
    fetchCallTableColumnsFailure,
    fetchCallTableColumnsSuccess,
    fetchSupervisedRoles,
    fetchSupervisedRolesFail,
    fetchSupervisedRolesSuccess,
    fetchUserSettings,
    fetchUserSettingsFailure,
    fetchUserSettingsSuccess,
    fetchVersions,
    settingsEffectsInitialized,
    toggleTheme,
    updateAudioSettings,
    updateUserSettings,
    updateUserSettingsFailure,
    updateUserSettingsSuccess
} from './settings.actions';
import { displayToastNotification } from '../../notification/+state/notification.actions';
import { ToastType } from '@msi/cobalt';
import { fetchLayoutConfigurationSuccess } from '../../configuration/+state/configuration.actions';
import { selectUserSettings } from './settings.selectors';
import { SkipperService } from '../../core/services/skipper.service';
import { concatLatestFrom } from '@ngrx/operators';
import { selectActiveAudioSettings, selectUserMediaDevices } from '../../call/+state/media.selectors';
import { setAlertVolume, setCallVolume, setIrrVolume, updateMediaDevices } from '../../call/+state/media.actions';
import { alertToDevice, alertToHeadsets, irrToDevice, irrToHeadsets, setPeripheralVolume } from '../../call/+state/cchub.actions';
import { alertToUsbHeadsets, irrToUsbHeadsets, usbAlertToDevice, usbIrrToDevice } from '../../usb/+state/usb.actions';

@Injectable()
export class SettingsEffects implements OnInitEffects {
    constructor(
        private store: Store,
        private skipperService: SkipperService,
        private actions$: Actions
    ) {}

    ngrxOnInitEffects() {
        return settingsEffectsInitialized();
    }

    init$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(settingsEffectsInitialized),
            switchMap(() => this.skipperService.initialized$),
            filter((initialized) => !!initialized),
            mergeMap(() => [fetchUserSettings(), fetchVersions(), fetchSupervisedRoles(), fetchAbandonedCallsRoutingSettings()])
        );
    });

    fetchAgencySettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchAgencySettings),
            switchMap(() =>
                this.skipperService.fetchSupervisorAgencySettings().pipe(
                    map((settings) => fetchAgencySettingsSuccess({ settings })),
                    catchError((err: Error) => of(fetchAgencySettingsFailure({ payload: err.message })))
                )
            )
        )
    );

    fetchStandaloneCallColumns$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchUserSettingsSuccess),
            switchMap(() =>
                this.skipperService.fetchStandaloneCallColumns().pipe(
                    map((config) => fetchCallTableColumnsSuccess({ config })),
                    catchError((err: Error) => of(fetchCallTableColumnsFailure({ payload: err?.message })))
                )
            )
        )
    );

    fetchSupervisorLayout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchLayoutConfigurationSuccess),
            filter((l) => l && l.config?.centralPane?.monitoring),
            map(() => fetchAgencySettings())
        )
    );

    fetchUserSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchUserSettings),
            switchMap(() =>
                defer(() => this.skipperService.fetchUserSettings()).pipe(
                    map((settings) => fetchUserSettingsSuccess({ settings })),
                    retry({ delay: () => timer(5000) }),
                    catchError((err: Error) => of(fetchUserSettingsFailure({ payload: err.message })))
                )
            )
        )
    );

    updateUserSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateUserSettings),
            mergeMap(({ settings }) =>
                this.skipperService.updateUserSettings(settings).pipe(
                    map(() => updateUserSettingsSuccess({ settings })),
                    catchError((err: Error) => of(updateUserSettingsFailure({ payload: err.message })))
                )
            )
        )
    );

    saveSettingsFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateUserSettingsFailure),
            map(({ payload }) => displayToastNotification({ message: payload, level: ToastType.error }))
        )
    );

    updateAudioSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateAudioSettings),
            debounceTime(200),
            concatLatestFrom(() => [this.store.select(selectUserSettings), this.store.select(selectActiveAudioSettings)]),
            map(([, allSettings, settings]) =>
                updateUserSettings({
                    settings: {
                        ...allSettings,
                        audioSettings: { ...allSettings.audioSettings, ...settings }
                    }
                })
            )
        )
    );

    saveTheme$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(toggleTheme),
            concatLatestFrom(() => this.store.select(selectUserSettings)),
            map(([_, settings]) => updateUserSettings({ settings }))
        );
    });

    fetchSupervisedRoles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fetchSupervisedRoles),
            exhaustMap((action) =>
                defer(() => this.skipperService.fetchSupervisedRoles()).pipe(
                    map((roles) => fetchSupervisedRolesSuccess({ roles: roles })),
                    retry({ delay: () => timer(5000) }),
                    catchError((err: Error) => of(fetchSupervisedRolesFail({ payload: err?.message })))
                )
            )
        );
    });

    applyAudioConfiguration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchUserSettingsSuccess),
            concatLatestFrom(() => this.store.select(selectUserMediaDevices)),
            filter(([{ settings }]) => Boolean(settings.audioSettings)),
            tap(() => console.debug('Applying saved user audio settings')),
            mergeMap(([{ settings }, mediaDevices]) => [
                // if the media devices were already acquired (and their effects which select the preferred device applied) then re-run the effects to recompute the selected device
                ...(Boolean(mediaDevices.length) ? [updateMediaDevices({ devices: mediaDevices })]: []),
                ...(settings.audioSettings.callOutputVolume !== undefined ? [setCallVolume({ volume: settings.audioSettings.callOutputVolume })] : []),
                ...(settings.audioSettings.alertOutputVolume !== undefined ? [setAlertVolume({ volume: settings.audioSettings.alertOutputVolume })] : []),
                ...(settings.audioSettings.irrOutputVolume !== undefined ? [setIrrVolume({ volume: settings.audioSettings.irrOutputVolume })] : []),
                ...(settings.audioSettings.alertToHeadsets !== undefined ? [alertToHeadsets({ alert: settings.audioSettings.alertToHeadsets })] : []),
                ...(settings.audioSettings.alertToHeadsets !== undefined ? [alertToUsbHeadsets({ alert: settings.audioSettings.alertToHeadsets })] : []),
                ...(settings.audioSettings.alertToDevice !== undefined ? [alertToDevice({ alert: settings.audioSettings.alertToDevice })] : []),
                ...(settings.audioSettings.alertToDevice !== undefined ? [usbAlertToDevice({ alert: settings.audioSettings.alertToDevice })] : []),
                ...(settings.audioSettings.irrToHeadsets !== undefined ? [irrToHeadsets({ alert: settings.audioSettings.irrToHeadsets })] : []),
                ...(settings.audioSettings.irrToHeadsets !== undefined ? [irrToUsbHeadsets({ alert: settings.audioSettings.irrToHeadsets })] : []),
                ...(settings.audioSettings.irrToDevice !== undefined ? [irrToDevice({ alert: settings.audioSettings.irrToDevice })] : []),
                ...(settings.audioSettings.irrToDevice !== undefined ? [usbIrrToDevice({ alert: settings.audioSettings.irrToDevice })] : []),
                ...(settings.audioSettings.volumeStatus !== undefined ? [...Object.entries(settings.audioSettings.volumeStatus)
                    .map(([key, value]) => setPeripheralVolume({ peripheral: key, volume: value.volume }))] : [])
                // Note: RTI/SAM volumes persisted on device and thus don't need to be restored from the saved profile
                // They can be set and loaded via RTI configuration if needed
            ])
        )
    );

    fetchAbandonedCallsRoutingSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchAbandonedCallsRoutingSettings),
            exhaustMap(() =>
                defer(() => this.skipperService.fetchAbandonedCallsRoutingSettings()).pipe(
                    map((settings) => fetchAbandonedCallsRoutingSettingsSuccess({ settings: settings })),
                    retry({ delay: () => timer(5000) }),
                    catchError((err: Error) => of(fetchAbandonedCallsRoutingSettingsFail({ payload: err?.message })))
                )
            )
        )
    );
}
